// The billing view's API (server/billing.ts → web/src/pages/billing.tsx), and the pricing both
// sides state: the server checks the Stripe price against it and grants by it, the page quotes it.

export const SEAT_PRICE_CENTS = 1000;
export const CREDITS_PER_SEAT = 20;

/** Credit packs, bought once by a subscribed org: the pack's dollars × 2 credits. */
export const PACKS = [10, 50, 100] as const;
export type Pack = (typeof PACKS)[number];
export const CREDITS_PER_PACK_DOLLAR = 2;
/** A pack's credits last this many calendar months from purchase. */
export const PACK_MONTHS = 12;
export const packCredits = (pack: Pack): number => pack * CREDITS_PER_PACK_DOLLAR;
export const isPack = (n: unknown): n is Pack => PACKS.includes(n as Pack);

/** GET /api/billing/credits: what the org can spend now, held credits already taken off. */
export interface Credits {
  credits: number;
}

/** `confirming`: back from a paid Checkout whose first invoice hasn't been granted yet. */
export type BillingState = "subscribed" | "registered" | "confirming";

/** GET /api/billing[?session=<Checkout Session id>]. */
export interface BillingInfo {
  state: BillingState;
  /** The org's verified members now: what a Checkout started now would bill, and what the billed
   * seats are set to (billing/seats.ts). */
  members: number;
  /** The quantity on the subscription's next invoice, after this request's seat sync; null with
   * no subscription, when Stripe couldn't be read, and for anyone but an org admin. */
  billedSeats: number | null;
  /** An admin's view couldn't read Stripe, so `billedSeats` is null for want of an answer, not
   * because nothing is billed. */
  seatsUnknown: boolean;
  /** The next invoice before tax: billed seats × the seat price. Null while an end date is set
   * (Stripe bills the period it falls in pro rata, so the figure would be wrong) and wherever
   * `billedSeats` is. */
  nextInvoiceCents: number | null;
  credits: number;
  /** Every live lot the credits come from, in spend order: what is left of it and when it ends. */
  lots: {
    id: string;
    kind: "seat" | "purchased" | "grant";
    remaining: number;
    expiresAt: string;
  }[];
  /** A credit pack can be bought: the org is subscribed (active or past_due), and not a system
   * org. Whether this viewer may buy is `isAdmin`. */
  canBuyPacks: boolean;
  /** Checkout can start: no live subscription (an unpaid or paused one blocks it too), and not a
   * system org. Whether this viewer may start it is `isAdmin`. */
  canSubscribe: boolean;
  /** Stripe's portal can open for the org: it has a live subscription. Whether this viewer may
   * open it is `isAdmin`. */
  canManage: boolean;
  /** When a cancellation scheduled in Stripe's portal takes effect (ISO), null with none; the org
   * stays subscribed until then. */
  endsAt: string | null;
  /** A renewal's payment failed and Stripe is retrying it (`past_due`): still subscribed, with no
   * seat credits for the new period until it is paid. */
  paymentRetrying: boolean;
  /** Automatic top-up, for every member to read: when the balance falls below `threshold`, the
   * `pack` is bought with the org's saved card. Null when it is off. */
  topUp: TopUpSetting | null;
  /** A refused charge turned top-up off, and no admin has saved the setting since. */
  topUpFailed: boolean;
  /** This request's token says org admin: the one who may subscribe. */
  isAdmin: boolean;
}

/** PUT /api/billing/topup's body when turning it on; `{pack: null}` turns it off. */
export interface TopUpSetting {
  pack: Pack;
  /** From 1 to the pack's credits, so one top-up always clears it (decision 16). */
  threshold: number;
}
